import React from 'react'
import styled from '@emotion/styled'
import { useQuery } from '@tanstack/react-query'
import { colors, font, spacing } from '@mobi/component-library/Theme/Common'
import { Icon } from '@mobi/component-library/Common/V2/Icon'
import { Button } from '@mobi/component-library/Common/V2/Buttons/Button'
import { addToast, ToastContent } from '@mobi/component-library/Feedback/Toast'
import type { BetSlipItem } from '@mobi/betslip/types'
import { calculateMultiReturn } from '@mobi/betslip/helpers/calculator/multis'
import { createBetSlipItem } from '@mobi/betslip/helpers/createBetSlipItem'
import { isFobSelection } from '@mobi/betslip/helpers/typeGuards'
import type { Tabs, Ticket } from '@core/Areas/AccountActivity/types'
import { QuickbetSelection, SetActiveInvestment } from '@core/Areas/Quickbet/signals'
import { convertToBetSelection } from '@core/Utils/betting/handleBetSelection/helpers/convertToBetSelection'
import { createTicketLoadBetDetails } from '@core/Areas/ShareBet/LoadBet/helpers/createTicketLoadBetDetails'
import { getLegStatus } from '@core/Areas/AccountActivity/Components/Ticket/helpers'
import { trackRebet } from '@classic/Foundation/Analytics/GoogleTagManagerService'
import { handleBetSelection } from '@core/Utils/betting/handleBetSelection/handleBetSelection'
import { getLoadBetErrorMessage } from '@core/Areas/ShareBet/LoadBet/helpers/loadBetErrors'
import { FOB_TICKET_PLACED_BET_TYPE } from '@core/Areas/ShareBet/LoadBet/constants'
import { loadBet } from '@core/Areas/ShareBet/LoadBet/helpers/loadBet'
import { canAllowReBetForTicket } from './helpers/canAllowReBetForTicket'

export const ReBetButton: React.FC<{ ticket: Ticket; activeTab: Tabs }> = ({
  ticket,
  activeTab,
}) => {
  if (!canAllowReBetForTicket(activeTab, ticket)) {
    return null
  }

  return <ReBetButtonLocal ticket={ticket} source={activeTab} />
}

// ===============
// Local Component
// ===============

const ReBetButtonLocal: React.FC<{ ticket: Ticket; source: Tabs }> = ({ ticket, source }) => {
  const pendingLegs = ticket.legs.filter(leg => getLegStatus(leg.result) === 'Pending')
  const loadDetails = createTicketLoadBetDetails({ ...ticket, legs: pendingLegs })

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['ticketodds', ticket.ticketSequence],
    gcTime: 3 * 60 * 1_000,
    staleTime: 3 * 60 * 1_000,
    queryFn: () => loadBet(loadDetails, { location: 'rebet' }),
  })

  React.useEffect(() => {
    if (isError && error) {
      addToast({
        id: 'rebet-load-failed',
        content: <ToastContent title={getLoadBetErrorMessage(error)} icon='error' />,
      })
    }
  }, [isError, error])

  const handleReBetClick = () => {
    if (isLoading || !data) return

    const investmentType =
      data.selections.length === 1 && loadDetails[0].betType === FOB_TICKET_PLACED_BET_TYPE
        ? 'place'
        : 'win'

    handleBetSelection({
      selections: data.selections,
      betFlow: {
        location: 'Quickbet',
        options: { setUp: () => SetActiveInvestment(investmentType) },
      },
    })

    const resultedCount = ticket.legs.length - pendingLegs.length
    trackRebet({ source, resultedCount, pendingCount: pendingLegs.length })
  }

  const { win, place } = calcOdds(data?.selections)

  return (
    <WrapperStyled>
      <h4>
        <Icon name='LineRepeat04' size='1.6rem' /> Re-Bet
      </h4>

      <Button
        size='xs'
        data-testid='rebet-button'
        color='secondary_grey'
        isLoading={isLoading || !data}
        onClick={handleReBetClick}
      >
        <div>{win.toFixed(2)}</div>
        <div>{place?.toFixed(2)}</div>
      </Button>
    </WrapperStyled>
  )
}

// =============
// Local Helpers
// =============

function calcOdds(data?: QuickbetSelection[]) {
  if (!data) {
    return { win: 0, place: 0 }
  }

  if (data.length === 1 && isFobSelection(data[0].selection)) {
    return { win: data[0].selection.winPrice, place: data[0].selection.placePrice }
  }

  const betSlipItems = data.map(bet => {
    const betSlipItem = createBetSlipItem(convertToBetSelection(bet))
    if (isFobSelection(bet.selection)) {
      betSlipItem.multiLegBetType = bet.selection.multiLegBetType
    }
    return betSlipItem
  })

  const win = calculateMultiReturn(betSlipItems as BetSlipItem[], { shouldRound: true })
  return { win, place: null }
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',

  h4: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.smx2,
    margin: 0,
    padding: `${spacing.smx1} ${spacing.smx2}`,
    textAlign: 'unset',
    fontSize: font.size.md.fontSize,
    letterSpacing: font.size.md.letterSpacing,
    lineHeight: font.size.md.lineHeight,
    fontWeight: font.weight.regular,
    color: colors.black,
  },

  button: {
    minWidth: '10rem',
    minHeight: '4.8rem',
    fontSize: font.size.lg.fontSize,
    lineHeight: '1.8rem',
    letterSpacing: font.size.lg.letterSpacing,
    color: colors.studio[900],
    fontWeight: font.weight.semibold,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
})
